import React, { useState } from 'react';
import { Modal, ScrollView, View, Text, StyleSheet } from 'react-native';
import { mdiArrowLeft } from '@mdi/js';
import { Picker } from '@react-native-picker/picker';
import GenericButton from '../molecules/GenericButton';
import RejectOfferModal from './RejectOfferModal';
import HeaderButton from '../layout/HeaderButton';
import Header from '../layout/Header';
import CredentialCard from '../../features/credential/card/CredentialCard';
import ProofDetails from '../../features/proof/ProofDetails';
import ProofSuccess from '../../features/proof/ProofSuccess';
import { IThemeContext, useThemeContext } from '../../contexts';
import { CredentialProofRequest } from '../../types/credentials';

interface ProofRequestModalProps {
  visible: boolean;
  onClose: () => void;
  proofRequest: CredentialProofRequest;
  primaryAction: { title: string; action: () => void };
  secondaryAction: { title: string; action: () => void };
  showSuccess?: boolean;
  selectedCredentialIndex: number;
  setSelectedCredentialIndex: (index: number) => void;
  onAccept: () => void;
}

/**
 * @description Proof request modal component
 * @param {ProofRequestModalProps} props - Component properties
 * @return {React.FC}
 */
const ProofRequestModal: React.FC<ProofRequestModalProps> = ({
  visible,
  onClose,
  proofRequest,
  primaryAction,
  secondaryAction,
  showSuccess,
  selectedCredentialIndex,
  setSelectedCredentialIndex,
  onAccept
}) => {
  const [rejectVisible, setRejectVisible] = useState(false);
  const { theme } = useThemeContext();
  const styles = createProofRequestModalStyles(theme);

  const selectedCredential = proofRequest.credentials?.[selectedCredentialIndex];
  const hasMultipleCredentials = proofRequest.credentials?.length > 1;
  const hasCredentials = proofRequest.credentials?.length > 0;

  const handleReject = () => {
    secondaryAction.action();
    onClose();
    setRejectVisible(false);
  };

  const handleRejectFromModal = () => {
    setRejectVisible(true);
  };

  return (
    <>
      <Modal visible={visible} onRequestClose={onClose} animationType="fade">
        <Header
          title={showSuccess ? 'Proof Request Completed' : 'Proof Request Details'}
          leftButton={<HeaderButton icon={mdiArrowLeft} onPress={onClose} />}
        />

        {showSuccess ? (
          <ProofSuccess proof={proofRequest} />
        ) : (
          <View style={styles.modalContent}>
            <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContainer}>
              <View style={styles.contentWrapper}>
                {/* Header Section */}
                <View style={styles.modalHeader}>
                  <Text style={styles.reviewRequestHeader}>Review Request</Text>
                  <Text style={styles.reviewRequestText}>
                    <Text style={styles.requesterText}>{proofRequest.requester}</Text> is requesting
                    information from your credential.
                  </Text>
                </View>

                {/* Credential Selection */}
                {hasMultipleCredentials && (
                  <View>
                    <Text style={styles.sectionHeader}>Select Credential</Text>
                    <Picker
                      selectedValue={selectedCredentialIndex}
                      onValueChange={(_itemValue, itemIndex) =>
                        setSelectedCredentialIndex(itemIndex)
                      }
                      style={styles.picker}>
                      {proofRequest.credentials?.map((cred: { name: string }, index: number) => (
                        <Picker.Item key={index} label={cred.name} value={index} />
                      ))}
                    </Picker>
                  </View>
                )}

                {hasCredentials ? (
                  <>
                    {/* Credential Card */}
                    <View>
                      <CredentialCard credential={selectedCredential} />
                    </View>

                    {/* Proof Details */}
                    <ProofDetails proof={{ ...proofRequest, credentials: [selectedCredential] }} />
                  </>
                ) : (
                  <View style={styles.noCredentialsContainer}>
                    <Text style={styles.noCredentialsHeader}>No Credentials Available</Text>
                    <Text style={styles.noCredentialsText}>
                      You do not have a credential available for this proof request.
                    </Text>
                  </View>
                )}
              </View>
            </ScrollView>

            {/* Action Buttons - Fixed at bottom */}
            <View style={styles.buttonContainer}>
              {hasCredentials && (
                <GenericButton
                  title={primaryAction.title}
                  onPress={onAccept}
                  buttonStyle={styles.primaryButton}
                  textStyle={styles.primaryButtonText}
                  isPrimary={true}
                />
              )}
              <GenericButton
                title={secondaryAction.title}
                onPress={handleRejectFromModal}
                buttonStyle={styles.secondaryButton}
                textStyle={styles.secondaryButtonText}
                isPrimary={false}
              />
            </View>
          </View>
        )}
      </Modal>

      <RejectOfferModal
        modalVisible={rejectVisible}
        setModalVisible={setRejectVisible}
        rejectAction={handleReject}
      />
    </>
  );
};

const createProofRequestModalStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    // Modal styles
    modalContent: {
      flex: 1,
      justifyContent: 'space-between'
    },
    scrollView: {
      flex: 1
    },
    scrollContainer: {
      padding: 24,
      paddingBottom: 0
    },
    contentWrapper: {
      maxWidth: 500,
      alignSelf: 'center',
      width: '100%',
      gap: 24
    },

    // Header styles
    modalHeader: {
      gap: 8
    },
    reviewRequestHeader: {
      fontWeight: '700',
      fontSize: 24,
      color: theme.textPrimary
    },
    reviewRequestText: {
      fontWeight: '400',
      fontSize: 16,
      color: theme.textSecondary,
      lineHeight: 24
    },
    requesterText: {
      fontWeight: '700',
      color: theme.textPrimary
    },
    sectionHeader: {
      fontWeight: '700',
      fontSize: 16,
      color: theme.textPrimary,
      marginBottom: 8
    },

    // Picker styles
    picker: {
      borderWidth: 1,
      borderColor: '#ccc',
      borderRadius: 6,
      padding: 10
    },

    // No credentials styles
    noCredentialsContainer: {
      gap: 8,
      paddingVertical: 16
    },
    noCredentialsHeader: {
      fontWeight: '700',
      fontSize: 20,
      color: theme.textPrimary
    },
    noCredentialsText: {
      fontWeight: '400',
      fontSize: 16,
      color: theme.textSecondary
    },

    // Button container styles
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 16,
      padding: 24,
      backgroundColor: 'white',
      borderTopWidth: 1,
      borderTopColor: '#E5E5E5'
    },
    primaryButton: {
      backgroundColor: theme.primary,
      flex: 1,
      height: 53,
      borderRadius: 6
    },
    primaryButtonText: {
      fontSize: 18,
      fontWeight: '700',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: 'white',
      flex: 1,
      height: 53,
      borderRadius: 6,
      borderColor: 'black',
      borderWidth: 1
    },
    secondaryButtonText: {
      fontSize: 18,
      fontWeight: '700',
      color: 'black'
    }
  });

export default ProofRequestModal;
